import { HomeLink } from '@/components/home-link';
import { RoundedButton } from '@/components/rounded-button';
import { LocalStorageKey } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { User } from '@/types';
import { truncateAddress } from '@/utils';
import { Box, Card, HStack, SimpleGrid, Text } from '@chakra-ui/react';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import { useLocalStorage } from 'usehooks-ts';
import { OnboardingStep } from './constants';

export const OnboardingHeader = ({
  currentStepNumber,
  goToPrevStep,
  goToNextStep,
  referredByUser,
  onboardingSteps,
}: {
  currentStepNumber: number;
  goToPrevStep: () => void;
  goToNextStep: () => void;
  referredByUser: User | undefined;
  onboardingSteps: OnboardingStep[];
}) => {
  return (
    <Card.Root borderRadius={[0, 0, 'full']} w="full" mt={[-8, -8, 0]}>
      <Card.Body>
        <SimpleGrid columns={[2, 2, 3]} alignItems="center">
          <HomeLink />
          <ProgressSteps
            currentStepNumber={currentStepNumber}
            goToPrevStep={goToPrevStep}
            onboardingSteps={onboardingSteps}
            goToNextStep={goToNextStep}
          />
          <UserDetails referredByUser={referredByUser} />
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
};

const ProgressSteps = ({
  currentStepNumber,
  goToPrevStep,
  goToNextStep,
  onboardingSteps,
}: {
  currentStepNumber: number;
  goToPrevStep: () => void;
  goToNextStep: () => void;
  onboardingSteps: OnboardingStep[];
}) => {
  const [furthestStepSeen] = useLocalStorage(LocalStorageKey.FurthestStepSeen, OnboardingStep.Welcome);
  const furthestStepSeenIndex = onboardingSteps.indexOf(furthestStepSeen);
  return (
    <HStack justifySelf="center" hideBelow="md">
      <RoundedButton size="xs" variant="outline" onClick={goToPrevStep} disabled={!currentStepNumber}>
        <HiOutlineChevronLeft />
      </RoundedButton>
      {onboardingSteps.map((step, idx) => (
        <Box
          key={step}
          w={16}
          h={1.5}
          bgColor={idx <= currentStepNumber ? 'colorPalette.950' : 'bg.muted'}
          borderRadius="full"
        />
      ))}
      <RoundedButton
        size="xs"
        variant="outline"
        onClick={goToNextStep}
        disabled={currentStepNumber >= furthestStepSeenIndex}
      >
        <HiOutlineChevronRight />
      </RoundedButton>
    </HStack>
  );
};

const UserDetails = ({ referredByUser }: { referredByUser: User | undefined }) => {
  const { logout } = useAuth();
  return (
    <HStack justifySelf="end" gap={6}>
      {referredByUser && (
        <HStack gap={1} fontWeight="500" fontSize="sm">
          <Text color="fg.muted">Invited by</Text>
          <Text>{truncateAddress(referredByUser.walletAddress)}</Text>
        </HStack>
      )}
      <RoundedButton variant="outline" onClick={logout} ml={['auto', 'auto', 0]}>
        Disconnect
      </RoundedButton>
    </HStack>
  );
};
